<template>
  <div class="pixel-game">
    <!-- Шапка -->
    <div class="game-header">
      <h1>🔬 Угадай физическое явление</h1>
      <div class="score-board">
        <span class="score">⭐ Баллы: <strong>{{ score }}</strong></span>
        <span class="streak">🔥 Серия: {{ streak }}</span>
      </div>
    </div>

    <!-- Canvas с картинкой -->
    <div class="canvas-wrap">
      <canvas ref="gameCanvas" width="800" height="500" />

      <!-- Оверлей результата -->
      <div v-if="answered" class="result-overlay" :class="{ correct: isCorrect, wrong: !isCorrect }">
        <div class="result-icon">{{ isCorrect ? '🎉' : '😕' }}</div>
        <div class="result-title">{{ isCorrect ? 'Правильно!' : 'Неверно' }}</div>
        <div class="result-answer">{{ currentQuestion.correct }}</div>
        <div class="result-desc">{{ currentQuestion.description }}</div>
        <button @click="nextQuestion" class="next-btn">Следующий вопрос →</button>
      </div>
    </div>

    <!-- Прогресс открытия -->
    <div class="progress-wrap">
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: revealedPercent + '%' }" />
      </div>
      <span class="progress-label">{{ revealedPercent }}% открыто</span>
    </div>

    <!-- Управление -->
    <div class="controls">
      <button
        @click="revealMore"
        :disabled="revealedPercent >= 100 || answered"
        class="btn btn-reveal"
      >
        🔍 Открыть ещё (+{{ REVEAL_STEP }}%)
      </button>
      <button
        @click="useHint"
        :disabled="answered || hintUsed || revealedPercent >= 100"
        class="btn btn-hint"
      >
        💡 Подсказка (−15 баллов)
      </button>
    </div>

    <!-- Подсказка -->
    <div v-if="hintText" class="hint-box">💡 {{ hintText }}</div>

    <!-- Варианты ответа -->
    <div class="options-grid">
      <button
        v-for="(option, idx) in currentQuestion?.options || []"
        :key="idx"
        @click="checkAnswer(option)"
        :disabled="answered"
        :class="['option-btn', getOptionClass(option)]"
      >
        <span class="opt-letter">{{ ['A', 'B', 'C', 'D'][idx] }}</span>
        <span class="opt-text">{{ option }}</span>
      </button>
    </div>

    <!-- Счётчик -->
    <div class="footer">
      <span>Вопрос {{ questionIndex + 1 }} из {{ totalQuestions }}</span>
      <span v-if="answered" class="points">+{{ lastPoints }} баллов</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'

/* ==================== КОНФИГУРАЦИЯ ==================== */
const CANVAS_W = 800
const CANVAS_H = 500
const TILE_SIZE = 24
const REVEAL_STEP = 10

/* ==================== СОСТОЯНИЕ ==================== */
const gameCanvas = ref(null)

const questions = ref([])
const questionIndex = ref(0)
const currentQuestion = ref({
  question: '',
  image: '',
  options: [],
  correct: '',
  hint: '',
  description: ''
})

const revealedPercent = ref(0)
const score = ref(0)
const streak = ref(0)
const answered = ref(false)
const isCorrect = ref(false)
const lastPoints = ref(0)
const hintUsed = ref(false)
const hintText = ref('')

// Внутренние данные для отрисовки
let tilePositions = []   // перемешанные позиции плиток
let imageObj = null      // загруженное изображение
let imgDrawInfo = null   // {x, y, w, h} — куда рисовать картинку

const totalQuestions = ref(0)

/* ==================== ЖИЗНЕННЫЙ ЦИКЛ ==================== */
onMounted(async () => {
  const res = await fetch(new URL('../questions.json', import.meta.url).href)
  questions.value = await res.json()
  totalQuestions.value = questions.value.length

  // Преобразуем имена файлов в полные URL
  questions.value.forEach(q => {
    q.image = new URL(`../sprites/FizPic/${q.image}`, import.meta.url).href
  })

  // Перемешиваем вопросы
  shuffleArray(questions.value)

  loadQuestion(0)
})

/* ==================== ИГРОВАЯ ЛОГИКА ==================== */
function loadQuestion(idx) {
  questionIndex.value = idx
  answered.value = false
  isCorrect.value = false
  revealedPercent.value = 0
  hintUsed.value = false
  hintText.value = ''
  lastPoints.value = 0
  tilePositions = []
  imageObj = null
  imgDrawInfo = null

  if (idx < questions.value.length) {
    currentQuestion.value = questions.value[idx]
    preloadImage()
  } else {
    // Все вопросы пройдены — начинаем заново
    shuffleArray(questions.value)
    loadQuestion(0)
  }
}

function nextQuestion() {
  loadQuestion(questionIndex.value + 1)
}

function preloadImage() {
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.onload = () => {
    imageObj = img
    computeImageLayout()
    generateTiles()
    draw()
  }
  img.src = currentQuestion.value.image
}

function revealMore() {
  if (revealedPercent.value >= 100 || answered.value) return
  revealedPercent.value = Math.min(100, revealedPercent.value + REVEAL_STEP)
  draw()
}

function useHint() {
  if (hintUsed.value || answered.value || score.value < 15) {
    if (score.value < 15) {
      hintText.value = 'Недостаточно баллов для подсказки!'
      return
    }
    return
  }
  hintUsed.value = true
  score.value = Math.max(0, score.value - 15)
  hintText.value = currentQuestion.value.hint
}

function checkAnswer(option) {
  if (answered.value) return
  answered.value = true
  isCorrect.value = option === currentQuestion.value.correct

  if (isCorrect.value) {
    streak.value++
    // Чем раньше угадал — тем больше баллов
    // 0% открыто = 100 баллов, 100% открыто = 10 баллов
    const p = revealedPercent.value
    const base = Math.max(10, Math.round(100 - p * 0.9))
    const bonus = streak.value >= 3 ? 10 : 0
    lastPoints.value = base + bonus
    score.value += lastPoints.value
  } else {
    streak.value = 0
    lastPoints.value = 0
  }

  // Показываем полную картинку
  revealedPercent.value = 100
  draw()
}

function getOptionClass(option) {
  if (!answered.value) return ''
  if (option === currentQuestion.value.correct) return 'correct'
  return 'wrong'
}

/* ==================== ОТРИСОВКА ==================== */
function computeImageLayout() {
  if (!imageObj) return
  const cAspect = CANVAS_W / CANVAS_H
  const iAspect = imageObj.width / imageObj.height

  let w, h, x, y
  if (iAspect > cAspect) {
    w = CANVAS_W
    h = imageObj.height * (CANVAS_W / imageObj.width)
    x = 0
    y = (CANVAS_H - h) / 2
  } else {
    h = CANVAS_H
    w = imageObj.width * (CANVAS_H / imageObj.height)
    x = (CANVAS_W - w) / 2
    y = 0
  }
  imgDrawInfo = { x, y, w, h }
}

function generateTiles() {
  if (!imgDrawInfo) return
  const { x: offX, y: offY, w: drawW, h: drawH } = imgDrawInfo
  const cols = Math.ceil(drawW / TILE_SIZE)
  const rows = Math.ceil(drawH / TILE_SIZE)

  tilePositions = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      tilePositions.push({
        x: offX + c * TILE_SIZE,
        y: offY + r * TILE_SIZE,
        w: Math.min(TILE_SIZE, offX + drawW - (offX + c * TILE_SIZE)),
        h: Math.min(TILE_SIZE, offY + drawH - (offY + r * TILE_SIZE)),
        row: r,
        col: c
      })
    }
  }

  shuffleArray(tilePositions)
}

function draw() {
  const canvas = gameCanvas.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')

  // Фон
  ctx.fillStyle = '#0f1525'
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H)

  if (!imageObj || !imgDrawInfo) {
    ctx.fillStyle = '#8b949e'
    ctx.font = '18px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('Загрузка изображения...', CANVAS_W / 2, CANVAS_H / 2)
    return
  }

  const { x, y, w, h } = imgDrawInfo

  // Рисуем изображение
  ctx.drawImage(imageObj, x, y, w, h)

  // Рамка вокруг изображения
  ctx.strokeStyle = 'rgba(88,166,255,0.3)'
  ctx.lineWidth = 2
  ctx.strokeRect(x - 1, y - 1, w + 2, h + 2)

  // Считаем сколько плиток скрыть
  const totalTiles = tilePositions.length
  const tilesToHide = Math.floor(totalTiles * (1 - revealedPercent.value / 100))

  // Рисуем плитки поверх (скрываем часть изображения)
  for (let i = tilesToHide; i < totalTiles; i++) {
    const t = tilePositions[i]

    // Основная плитка
    ctx.fillStyle = '#1a2332'
    ctx.fillRect(t.x, t.y, t.w, t.h)

    // Обводка
    ctx.strokeStyle = 'rgba(88,166,255,0.15)'
    ctx.lineWidth = 1
    ctx.strokeRect(t.x + 0.5, t.y + 0.5, t.w - 1, t.h - 1)

    // Мини-иконка в центре плитки
    ctx.fillStyle = 'rgba(88,166,255,0.08)'
    ctx.font = `${Math.min(t.w, t.h) * 0.5}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('?', t.x + t.w / 2, t.y + t.h / 2)
  }

  // Если отвечено правильно — небольшой эффект свечения по краям изображения
  if (answered.value && isCorrect.value) {
    ctx.shadowColor = '#4ade80'
    ctx.shadowBlur = 20
    ctx.strokeStyle = 'rgba(74,222,128,0.6)'
    ctx.lineWidth = 3
    ctx.strokeRect(x - 2, y - 2, w + 4, h + 4)
    ctx.shadowBlur = 0
  }
}

/* ==================== УТИЛИТЫ ==================== */
function shuffleArray(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
}

// Перерисовываем при изменении revealedPercent
watch(revealedPercent, draw)
</script>

<style scoped>
.pixel-game {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Segoe UI', system-ui, sans-serif;
  color: #c9d1d9;
}

/* Шапка */
.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 10px;
}

.game-header h1 {
  font-size: 22px;
  margin: 0;
  color: #58a6ff;
}

.score-board {
  display: flex;
  gap: 16px;
  font-size: 15px;
}

.score strong {
  color: #fbbf24;
}

.streak {
  color: #f87171;
}

/* Canvas */
.canvas-wrap {
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #30363d;
  background: #0f1525;
  aspect-ratio: 8 / 5;
}

.canvas-wrap canvas {
  display: block;
  width: 100%;
  height: 100%;
}

/* Оверлей результата */
.result-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.96); }
  to   { opacity: 1; transform: scale(1); }
}

.result-icon {
  font-size: 48px;
  margin-bottom: 8px;
}

.result-title {
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 6px;
}

.result-answer {
  font-size: 18px;
  color: #fbbf24;
  font-weight: 600;
  margin-bottom: 10px;
}

.result-desc {
  font-size: 13px;
  color: #8b949e;
  max-width: 600px;
  text-align: center;
  line-height: 1.5;
  padding: 0 20px;
  margin-bottom: 16px;
}

.next-btn {
  padding: 10px 24px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  background: #238636;
  color: white;
  transition: background 0.2s;
}

.next-btn:hover {
  background: #2ea043;
}

/* Прогресс */
.progress-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 14px auto;
  max-width: 800px;
}

.progress-track {
  flex: 1;
  height: 10px;
  background: #21262d;
  border-radius: 5px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #1f6feb, #58a6ff);
  border-radius: 5px;
  transition: width 0.4s ease;
}

.progress-label {
  font-size: 13px;
  color: #8b949e;
  min-width: 90px;
  text-align: right;
}

/* Управление */
.controls {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;
}

.btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.btn-reveal {
  background: #1f6feb;
  color: white;
}

.btn-reveal:hover:not(:disabled) {
  background: #388bfd;
}

.btn-hint {
  background: #21262d;
  color: #c9d1d9;
  border-color: #30363d;
}

.btn-hint:hover:not(:disabled) {
  background: #30363d;
}

/* Подсказка */
.hint-box {
  text-align: center;
  padding: 10px 16px;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 8px;
  color: #fbbf24;
  font-size: 13px;
  max-width: 600px;
  margin: 0 auto 14px;
}

/* Варианты ответа */
.options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  max-width: 600px;
  margin: 0 auto;
}

.option-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid #30363d;
  border-radius: 10px;
  background: #161b22;
  color: #c9d1d9;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
}

.option-btn:hover:not(:disabled) {
  background: #21262d;
  border-color: #58a6ff;
}

.option-btn:disabled {
  cursor: default;
}

.option-btn.correct {
  background: rgba(35, 134, 54, 0.2);
  border-color: #238636;
  color: #7ee787;
}

.option-btn.wrong {
  background: rgba(248, 81, 73, 0.15);
  border-color: #f85149;
  color: #ff7b72;
  opacity: 0.7;
}

.opt-letter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #30363d;
  color: #58a6ff;
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;
}

.option-btn.correct .opt-letter {
  background: #238636;
  color: white;
}

.option-btn.wrong .opt-letter {
  background: #f85149;
  color: white;
}

/* Подвал */
.footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #21262d;
  font-size: 13px;
  color: #8b949e;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.points {
  color: #fbbf24;
  font-weight: 700;
}

@media (max-width: 640px) {
  .options-grid {
    grid-template-columns: 1fr;
  }
  .game-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
